import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, BookmarkSquareIcon, ChevronRightIcon, ChevronDownIcon, CommandLineIcon, StarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../../api/axios';
import { databaseMigrationsApi, type DryRunResult, type FolderMigration } from '../../api/databaseMigrations';
import { schemaTemplatesApi, type SchemaMasterConfig } from '../../api/schemaTemplates';
import SqlEditor from './components/SqlEditor';
import SavedScriptPicker from './components/SavedScriptPicker';
import DryRunPreviewModal from './components/DryRunPreviewModal';
import RunProgressPanel from './components/RunProgressPanel';
import RollbackPromptModal from './components/RollbackPromptModal';
import RunHistoryTable from './components/RunHistoryTable';
import MigrationsList from './components/migrations/MigrationsList';
import BulkApplyModal from './components/migrations/BulkApplyModal';
import BulkRunDrawer from './components/migrations/BulkRunDrawer';
import MarkAppliedModal from './components/migrations/MarkAppliedModal';
import { useBulkRun } from './components/migrations/hooks/useBulkRun';
import type { MigrationRowData, TargetSummary } from './components/migrations/utils/constants';
import { deriveStatus } from './components/migrations/utils/statusDerivation';

interface Company {
  id: string;
  company_name: string;
  company_code: string;
  database_name: string;
}

type Completion = { status: 'success' | 'failed'; failedTarget?: string; succeededTargets: string[]; errorMessage?: string };

interface TenantMigrationsTabProps {
  onNewCountChange?: (count: number) => void;
}

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.message || anyE?.message || 'Unknown error';
}

/** Convert a FolderMigration from the API to the new MigrationRowData shape. */
function toRowData(m: FolderMigration): MigrationRowData {
  // Normalize appliedTo.status to the union type
  const appliedTo: MigrationRowData['appliedTo'] = {};
  for (const [db, info] of Object.entries(m.appliedTo)) {
    const raw = info.status;
    // `resolved` = fixed by hand (counts as done); `partial` / `interrupted`
    // need attention like a failure — see the Status tab for the run.
    const status: 'success' | 'failed' | 'pending' =
      raw === 'success' || raw === 'resolved' ? 'success'
        : raw === 'failed' || raw === 'partial' || raw === 'interrupted' ? 'failed'
          : 'pending';
    // Carry the server's error classification through — without it a failure
    // caused by "the change is already there" is indistinguishable from a real
    // one, which is what made these migrations look permanently unapplied.
    appliedTo[db] = {
      version: info.version,
      lastRun: info.lastRun,
      status,
      errorKind: info.errorKind,
      errorReason: info.errorReason,
      errorDetail: info.errorDetail,
      errorCode: info.errorCode,
    };
  }
  return {
    id: m.scriptId,
    folderName: m.folder,
    fileName: m.filename,
    folderPath: m.folderPath,
    version: m.version,
    scope: m.scope,
    scopeInferred: m.scopeInferred,
    contentChanged: m.contentChangedSinceLast,
    hash: m.contentHash,
    appliedTo,
    sqlPreview: undefined,
    // Must test for a SUCCESS, not merely for a run. appliedTo now carries
    // failed targets too (so the UI can explain why), and counting those as
    // "ever applied" would mark a migration that has only ever failed as
    // applied whenever no target is selected.
    everApplied: Object.values(appliedTo).some((a) => a.status === 'success'),
    archived: m.archived,
    archivedAt: m.archivedAt,
    archivedReason: m.archivedReason,
  };
}

const TenantMigrationsTab: React.FC<TenantMigrationsTabProps> = (props) => {
  const [sql, setSql] = useState('');
  const [scriptId, setScriptId] = useState<number | null>(null);
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [backupTaken, setBackupTaken] = useState(false);
  const [completion, setCompletion] = useState<Completion | null>(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [folderActionError, setFolderActionError] = useState('');

  const [savingName, setSavingName] = useState('');
  const [savingDesc, setSavingDesc] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Schema Source (the tenant the provisioning template auto-refreshes from —
  // configured on the dedicated Schema Templates page). Surfaced here too so
  // it's never invisible whether a run will keep the template current.
  const [schemaMaster, setSchemaMaster] = useState<SchemaMasterConfig | null>(null);
  useEffect(() => {
    schemaTemplatesApi.getMaster().then(setSchemaMaster).catch(() => undefined);
  }, [historyKey]);

  // Companies list for building TargetSummary[]
  const [companies, setCompanies] = useState<Company[]>([]);
  useEffect(() => {
    api.get('/super-admin/companies').then((r) => setCompanies(r.data.companies || []));
  }, []);

  // Folder migrations list
  const [folderMigrations, setFolderMigrations] = useState<FolderMigration[]>([]);

  // Build TargetSummary[] from companies.
  // id === databaseName so that selectedTargetIds, bulk-run targets[], and SSE
  // echoes all use the same string ('cma_tenant_xyz').
  const availableTargets: TargetSummary[] = useMemo(
    () => companies.map((c) => ({
      id: c.database_name,
      name: c.company_name,
      databaseName: c.database_name,
      kind: 'tenant' as const,
    })),
    [companies],
  );

  // Map FolderMigration[] to MigrationRowData[]
  const migrationRows: MigrationRowData[] = useMemo(
    () => folderMigrations.map(toRowData),
    [folderMigrations],
  );

  // Emit "new" count to parent for tab badge. Counts what the list shows by
  // default: scope-inferred legacy files live behind the "Inferred scope"
  // toggle, so counting them made the badge promise rows the list hid.
  useEffect(() => {
    const count = migrationRows.filter((r) => !r.archived && !r.scopeInferred && deriveStatus(r, []) === 'new').length;
    props.onNewCountChange?.(count);
  }, [migrationRows, props.onNewCountChange]);

  // Selected targets as a Set<string> of company IDs
  const [selectedTargetIds, setSelectedTargetIds] = useState<Set<string>>(() => new Set());

  // Keep legacy targets[] in sync for the existing dry-run / run / rollback flows
  const targets = useMemo(
    () => availableTargets
      .filter((t) => selectedTargetIds.has(t.id))
      .map((t) => t.databaseName),
    [availableTargets, selectedTargetIds],
  );

  // Both /folders and /pending re-scan the migrations directory and
  // auto-register any new file on every call. Refiring on every single
  // target-picker checkbox click (targets changes on each click) sent a
  // burst of overlapping requests — harmless individually, but rapid
  // concurrent scans could race on registering the SAME brand-new file
  // (fixed server-side too), and it's wasted work regardless. Debounced so a
  // quick multi-select settles into one request instead of N.
  const latestRequestId = useRef(0);
  const refetchMigrations = useCallback(() => {
    const requestId = ++latestRequestId.current;
    const request = targets.length > 0
      ? databaseMigrationsApi.listPending(targets)
      : databaseMigrationsApi.listFolderMigrations();
    request.then((list) => {
      if (requestId !== latestRequestId.current) return; // superseded by a newer request
      setFolderMigrations(list.filter((m) => m.scope === 'tenant'));
    });
  }, [targets]);
  useEffect(() => {
    const timer = setTimeout(refetchMigrations, 250);
    return () => clearTimeout(timer);
  }, [historyKey, refetchMigrations]);

  // Bulk run state
  const { state: bulkState, start: startBulkRun, cancel: cancelBulkRun, reset: resetBulkRun } = useBulkRun();
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<MigrationRowData[]>([]);
  const [bulkTargetIds, setBulkTargetIds] = useState<string[]>([]);

  // Mark as applied state
  const [markAppliedRow, setMarkAppliedRow] = useState<MigrationRowData | null>(null);

  const handleDryRun = async () => {
    const result = await databaseMigrationsApi.dryRun({ sql, targets });
    setDryRun(result);
  };

  const handleExecute = async ({ backup }: { backup: boolean }) => {
    setBackupTaken(backup);
    setDryRun(null);
    const id = await databaseMigrationsApi.run({ sql, targets, backup, scriptId });
    setRunId(id);
  };

  const handleComplete = (result: Completion) => {
    setCompletion(result);
    setHistoryKey((k) => k + 1);
  };

  const handleRollback = async (toRoll: string[]) => {
    if (!runId) return;
    await databaseMigrationsApi.rollback(runId, toRoll);
  };

  const handleSaveScript = async () => {
    if (!savingName || !sql) return;
    await databaseMigrationsApi.createScript({
      name: savingName,
      description: savingDesc,
      sql_content: sql,
      target_scope: 'tenant',
    });
    setShowSaveDialog(false);
    setSavingName('');
    setSavingDesc('');
  };

  const handleApplyMigration = useCallback(
    (row: MigrationRowData, targetIds: string[]) => {
      if (targetIds.length === 0) return;
      setBulkRows([row]);
      setBulkTargetIds(targetIds);
      setBulkDrawerOpen(true);
      startBulkRun([row.id], targetIds, { backup: true });
    },
    [startBulkRun],
  );

  const handlePreviewMigration = async (row: MigrationRowData) => {
    setFolderActionError('');
    try {
      const { script } = await databaseMigrationsApi.getScript(row.id);
      setSql(script.sql_content);
      setScriptId(row.id);
    } catch (e) {
      setFolderActionError(errMsg(e));
    }
  };

  const handleBulkApply = useCallback((rows: MigrationRowData[], targetIds: string[]) => {
    setBulkRows(rows);
    setBulkTargetIds(targetIds);
    setBulkModalOpen(true);
  }, []);

  const handleBulkConfirm = useCallback(
    async ({ mode, backup, reason }: { mode: 'run' | 'mark'; backup: boolean; reason: string }) => {
      setBulkModalOpen(false);
      if (mode === 'mark') {
        try {
          const migrationIds = bulkRows.map((r) => r.id);
          const targetDbs = Array.from(bulkTargetIds);
          await databaseMigrationsApi.markApplied(migrationIds, targetDbs, reason);
          await refetchMigrations();
        } catch (err) {
          console.error('Bulk mark-applied failed:', err);
          alert(err instanceof Error ? err.message : 'Bulk mark-applied failed');
        }
      } else {
        setBulkDrawerOpen(true);
        const migrationIds = bulkRows.map((r) => r.id);
        startBulkRun(migrationIds, bulkTargetIds, { backup });
      }
    },
    [bulkRows, bulkTargetIds, startBulkRun, refetchMigrations],
  );

  const handleMarkApplied = useCallback((row: MigrationRowData) => {
    setMarkAppliedRow(row);
  }, []);

  // Auto-refetch migration list when a bulk run (including single-apply) finishes
  useEffect(() => {
    if (bulkState.status === 'complete' || bulkState.status === 'aborted' || bulkState.status === 'failed') {
      void refetchMigrations();
    }
  }, [bulkState.status, refetchMigrations]);

  const handleCopyReport = useCallback(() => {
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
    const successEvents = bulkState.events.filter((e) => e.type === 'success');
    const skippedEvents = bulkState.events.filter((e) => e.type === 'skipped');
    const failureEvents = bulkState.events.filter((e) => e.type === 'failure');
    const lines: string[] = [
      `Bulk migration run — ${now}`,
      `${bulkState.completedOperations} of ${bulkState.totalOperations} migrations x ${bulkTargetIds.length} targets applied`,
      '',
    ];
    for (const ev of successEvents) {
      const row = bulkRows.find((r) => r.id === ev.migrationId);
      const target = availableTargets.find((t) => t.id === ev.target);
      lines.push(`[OK] ${row?.folderName ?? ev.migrationId} -> ${target?.name ?? ev.target}`);
    }
    for (const ev of skippedEvents) {
      const row = bulkRows.find((r) => r.id === ev.migrationId);
      const target = availableTargets.find((t) => t.id === ev.target);
      lines.push(`[SKIP] ${row?.folderName ?? ev.migrationId} -> ${target?.name ?? ev.target} — already applied`);
    }
    for (const ev of failureEvents) {
      const row = bulkRows.find((r) => r.id === ev.migrationId);
      const target = availableTargets.find((t) => t.id === ev.target);
      lines.push(`[FAIL] ${row?.folderName ?? ev.migrationId} -> ${target?.name ?? ev.target} — ${ev.error ?? 'unknown error'}`);
    }
    navigator.clipboard.writeText(lines.join('\n')).catch(() => undefined);
  }, [bulkState, bulkRows, bulkTargetIds, availableTargets]);

  const canDryRun = sql.trim().length > 0 && targets.length > 0;

  const masterInCurrentSelection = !schemaMaster?.databaseName || targets.length === 0
    ? true // nothing selected yet, or no master configured — not this component's warning to show
    : targets.includes(schemaMaster.databaseName);
  const schemaStripWarn = !schemaMaster?.companyId
    || schemaMaster.hasMigrationsNewerThanTemplate
    || !masterInCurrentSelection;

  return (
    <div className="px-6 py-6 space-y-5">
      <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-xs ${
        schemaStripWarn ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'
      }`}>
        {schemaStripWarn ? <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" /> : <StarIcon className="w-4 h-4 flex-shrink-0" />}
        {!schemaMaster?.companyId ? (
          <span>No Schema Source tenant designated — new companies are provisioned from whatever the template last happened to contain.</span>
        ) : !masterInCurrentSelection ? (
          <span><strong>Schema Source:</strong> {schemaMaster.companyName} — not included in the current target selection, so this run won't refresh the provisioning template.</span>
        ) : schemaMaster.hasMigrationsNewerThanTemplate ? (
          <span><strong>Schema Source:</strong> {schemaMaster.companyName} has migrations newer than the saved template.</span>
        ) : (
          <span><strong>Schema Source:</strong> {schemaMaster.companyName} — template auto-refreshes when this tenant is migrated.</span>
        )}
        <Link to="/schema-templates" className="ml-auto font-semibold whitespace-nowrap hover:underline">
          {schemaMaster?.companyId ? 'Manage' : 'Configure'} →
        </Link>
      </div>
      {folderActionError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 mb-3 flex items-center justify-between gap-2">
          <span>{folderActionError}</span>
          <button onClick={() => setFolderActionError('')} className="text-red-400 hover:text-red-600">×</button>
        </div>
      )}
      <MigrationsList
        tabKind="tenant"
        rows={migrationRows}
        availableTargets={availableTargets}
        selectedTargetIds={Array.from(selectedTargetIds)}
        onTargetsChange={(ids) => setSelectedTargetIds(new Set(ids))}
        onApplyMigration={handleApplyMigration}
        onPreviewMigration={handlePreviewMigration}
        onBulkApply={handleBulkApply}
        onMarkApplied={handleMarkApplied}
        onArchiveChanged={refetchMigrations}
      />

      {/* Ad-hoc SQL is a different workflow from tracked folder migrations —
          collapsed by default so the list above is the primary, uncluttered view. */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-slate-600 hover:bg-slate-50"
        >
          {showAdvanced ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
          <CommandLineIcon className="w-4 h-4 text-slate-400" />
          Advanced: run ad-hoc SQL instead of a tracked migration
        </button>
        {showAdvanced && (
          <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-4">
            <SavedScriptPicker scope="tenant" onLoad={(id, content) => { setScriptId(id); setSql(content); }} />
            <SqlEditor value={sql} onChange={(v) => { setSql(v); setScriptId(null); }} />
            <div className="flex items-center gap-2">
              <button
                onClick={handleDryRun}
                disabled={!canDryRun}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <EyeIcon className="w-4 h-4" /> Dry-run preview
              </button>
              <button
                onClick={() => setShowSaveDialog(true)}
                disabled={!sql.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                <BookmarkSquareIcon className="w-4 h-4" /> Save as script
              </button>
            </div>
          </div>
        )}
      </div>

      {runId && !completion && (
        <RunProgressPanel
          key={runId}
          runId={runId}
          targets={targets}
          onComplete={handleComplete}
        />
      )}

      <RunHistoryTable scriptId={scriptId ?? undefined} refreshKey={historyKey} />

      {dryRun && (
        <DryRunPreviewModal
          result={dryRun}
          targets={targets}
          isRegistry={false}
          onClose={() => setDryRun(null)}
          onConfirm={handleExecute}
        />
      )}

      {completion?.status === 'failed' && completion.failedTarget && (
        <RollbackPromptModal
          succeededTargets={completion.succeededTargets}
          failedTarget={completion.failedTarget}
          errorMessage={completion.errorMessage ?? ''}
          backupTaken={backupTaken}
          onClose={() => setCompletion(null)}
          onRollback={handleRollback}
        />
      )}

      {showSaveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3">
            <h3 className="text-lg font-semibold text-slate-900">Save migration script</h3>
            <input
              type="text" value={savingName} onChange={(e) => setSavingName(e.target.value)}
              placeholder="Name (e.g. add-break-reminders)"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            <textarea
              value={savingDesc} onChange={(e) => setSavingDesc(e.target.value)}
              placeholder="Description (optional)" rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowSaveDialog(false)} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleSaveScript} disabled={!savingName} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk apply confirmation modal */}
      <BulkApplyModal
        open={bulkModalOpen}
        rows={bulkRows}
        targets={availableTargets.filter((t) => bulkTargetIds.includes(t.id))}
        onCancel={() => setBulkModalOpen(false)}
        onConfirm={handleBulkConfirm}
      />

      {/* Bulk run progress drawer */}
      <BulkRunDrawer
        open={bulkDrawerOpen}
        rows={bulkRows}
        targets={availableTargets.filter((t) => bulkTargetIds.includes(t.id))}
        state={bulkState}
        onClose={() => {
          if (bulkState.status === 'running') cancelBulkRun();
          setBulkDrawerOpen(false);
          resetBulkRun();
        }}
        onCopyReport={handleCopyReport}
      />

      {/* Mark as applied confirmation modal */}
      <MarkAppliedModal
        open={markAppliedRow !== null}
        row={markAppliedRow}
        targets={availableTargets.filter((t) => selectedTargetIds.has(t.id))}
        onCancel={() => setMarkAppliedRow(null)}
        onConfirm={async (reason) => {
          if (!markAppliedRow) return;
          const targetDbs = availableTargets
            .filter((t) => selectedTargetIds.has(t.id))
            .map((t) => t.databaseName);
          try {
            await databaseMigrationsApi.markApplied([markAppliedRow.id], targetDbs, reason);
            await refetchMigrations();
          } catch (err) {
            console.error('Mark applied failed:', err);
            alert(err instanceof Error ? err.message : 'Failed to mark migration as applied');
          } finally {
            setMarkAppliedRow(null);
          }
        }}
      />
    </div>
  );
};

export default TenantMigrationsTab;
